import { initZohoDesk } from './zoho-desk/init';
import { initZohoBooks } from './zoho-books/init';
import { getAppCodeFromURL } from '@/lib/utils/url';

// ── Hash → Search param promotion (Zoho CRM Workflow Custom Action widget only) ──
// Zoho CRM Workflow Custom Actions embed our app and append their own SDK
// bootstrap parameters AFTER our hash fragment, producing URLs like:
//   `/app?appCode=forzohocrm...#workflow-templates&serviceOrigin=...&frameorigin=...`
// The Zoho Embedded App SDK reads `serviceOrigin` / `frameorigin` from
// `location.search` to establish the parent-window postMessage handshake.
// When those params live in the hash instead of the query string, the SDK
// can't find them — PageLoad never fires and later calls such as
// `ZOHO.CRM.ACTION.setConfig(...)` fail with "Parentwindow reference not
// found". Detect and relocate these params to the search string BEFORE the
// SDK script is appended to the DOM.
//
// Scoped strictly to the Zoho CRM workflow widget frame —
// i.e. appCode is Zoho CRM AND hash id is `workflow-templates`.
if (typeof window !== 'undefined') {
    try {
        const bootstrapParams = new URLSearchParams(window.location.search);
        const bootstrapAppCode = (bootstrapParams.get('appCode') || '').toLowerCase();
        const isZohoCrm = bootstrapAppCode.includes('forzohocrm');
        const rawHash = window.location.hash.replace(/^#/, '');
        // Accept `?` or `&` as the separator between the hash id and appended params.
        const sepMatch = rawHash.match(/^([^?&/#]*)[?&](.+)$/);
        const hashId = sepMatch ? decodeURIComponent(sepMatch[1]) : '';
        if (isZohoCrm && sepMatch && hashId === 'workflow-templates') {
            const appended = new URLSearchParams(sepMatch[2]);
            // Only promote known SDK-bootstrap params — don't touch anything else.
            const PROMOTABLE = ['serviceOrigin', 'frameorigin', 'dc'] as const;
            let promoted = false;
            const searchParams = new URLSearchParams(window.location.search);
            PROMOTABLE.forEach((key) => {
                const val = appended.get(key);
                if (val !== null && !searchParams.has(key)) {
                    searchParams.set(key, val);
                    appended.delete(key);
                    promoted = true;
                }
            });
            if (promoted) {
                const remaining = appended.toString();
                const newHash = sepMatch[1] + (remaining ? '&' + remaining : '');
                const newUrl = `${window.location.pathname}?${searchParams.toString()}${newHash ? '#' + newHash : ''}`;
                window.history.replaceState(null, '', newUrl);
                console.log('[SDK] Promoted hash-appended SDK params to search:', PROMOTABLE.filter((k) => searchParams.has(k)));
            }
        }
    } catch (err) {
        console.warn('[SDK] hash→search promotion failed:', err);
    }

    const params = new URLSearchParams(window.location.search);
    // appCode may be in the query string (?appCode=…) OR in the path
    // (/twilioformonday/…) — Monday embeds the app via the path form, so
    // checking only the search param missed it and the Monday SDK was never
    // loaded. Fall back to the path-based resolver (search param wins to
    // preserve existing behavior for apps that pass ?appCode=).
    const appCode = params.get('appCode') || getAppCodeFromURL() || '';
    const appCodeLower = appCode.toLowerCase();
    const disableSDK = params.get('disableSAASSdk') === 'true' || params.get('target') === 'appcursor';

    // Skip all SaaS SDK initialization when disableSAASSdk=true or target=appcursor (standalone mode)
    if (!disableSDK) {
        // Zoho CRM / Zoho Bigin / Zoho Recruit — load embedded app SDK eagerly
        if (appCodeLower.includes('forzohocrm') || appCodeLower.includes('forzohobigin') || appCodeLower.includes('forzohorecruit')) {
            if (!(window as any).ZOHO) {
                const script = document.createElement('script');
                script.src = 'https://live.zwidgets.com/js-sdk/1.5/ZohoEmbededAppSDK.min.js';
                script.async = true;
                script.onload = () => {
                    console.log('[SDK] Zoho Embedded App SDK loaded');
                };
                document.head.appendChild(script);
            }
        }

        if (appCodeLower.includes('forzohodesk')) {
            if (!(window as any).deskSDKApp) {
                initZohoDesk();
            }
        }

        if (appCodeLower.includes('forzohobooks')) {
            if (!(window as any).booksSDKApp) {
                initZohoBooks();
            }
        }

        // Load Freshdesk/Freshworks SDK (fresh_client.js) — provides app.initialized() for widget context
        if (appCodeLower.includes('forfreshdesk') || appCodeLower.includes('forfreshworkscrm')) {
            if (!(window as any).app) {
                const script = document.createElement('script');
                script.src = 'https://static.freshdev.io/fdk/2.0/assets/fresh_client.js';
                script.async = true;
                script.onload = () => {
                    console.log('[SDK] Freshdesk/Freshworks SDK loaded');
                };
                document.head.appendChild(script);
            }
        }

        // Monday.com — load Monday SDK eagerly for pre-auth email resolution
        if (appCodeLower.includes('formonday')) {
            if (!(window as any).mondaySdk) {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/monday-sdk-js/dist/main.js';
                script.async = true;
                script.onload = () => {
                    console.log('[SDK] Monday SDK loaded');
                };
                document.head.appendChild(script);
            }
        }

        // Bitrix24 — load BX24 SDK eagerly
        if (appCodeLower.includes('forbitrix24')) {
            if (!(window as any).BX24) {
                const script = document.createElement('script');
                script.src = 'https://api.bitrix24.com/api/v1/';
                script.async = true;
                script.onload = () => {
                    console.log('[SDK] Bitrix24 SDK loaded');
                };
                document.head.appendChild(script);
            }
        }

        // Zendesk Sell / Zendesk Support — load ZAF SDK eagerly
        if (appCodeLower.includes('forzendesksell') || appCodeLower.includes('forzendesksupport')) {
            // Eagerly resize the Zendesk Support iframe to 100% × 600px as soon
            // as the SDK is available — the saas module's `onInit()` also fires
            // this, but only after auth. Without this, the Google sign-in step
            // renders inside a tiny default iframe. Scoped to Support; Sell
            // doesn't need the same default height.
            const fireSupportResize = () => {
                if (!appCodeLower.includes('forzendesksupport')) return;
                try {
                    const zaf = (window as any).ZAFClient;
                    if (!zaf || typeof zaf.init !== 'function') return;
                    const client = zaf.init();
                    if (client && typeof client.invoke === 'function') {
                        client.invoke('resize', { width: '100%', height: '600px' });
                    }
                } catch (err) {
                    console.warn('[SDK] Zendesk Support eager resize failed:', err);
                }
            };

            if (!(window as any).ZAFClient) {
                const script = document.createElement('script');
                script.src = 'https://static.zdassets.com/zendesk_app_framework_sdk/2.0/zaf_sdk.min.js';
                script.async = true;
                script.onload = () => {
                    console.log('[SDK] Zendesk ZAF SDK loaded');
                    fireSupportResize();
                };
                document.head.appendChild(script);
            } else {
                // Script already on window (hot reload, cached) — resize now.
                fireSupportResize();
            }
        }
    }
}
